<script setup>
// Nexus IA → Relatório de disparos (custom/app/controllers/api/v1/accounts/neuxs/disparos_controller.rb):
// o que o SGP disparou, o que foi entendido e o que saiu — ou por que não saiu.
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import Button from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import { createNexusApi, erroDaApi } from './api';
import messages from './i18n';

const props = defineProps({
  accountId: { type: [Number, String], required: true },
});

const { t } = useI18n({ messages });
const EXEMPLO_DE_CHAVE = 'fatura_vencendo';
const api = computed(() => createNexusApi(props.accountId));

const ESTADOS = [
  'enviado',
  'ensaio',
  'recusado',
  'falhou',
  'ignorado',
  'na_fila',
  'enviando',
];
const RESUMO = ['enviado', 'ensaio', 'recusado', 'falhou', 'ignorado'];
const PERIODOS = [1, 7, 30, 90];
// As classes ficam no .vue: o Tailwind não lê os .js desta pasta.
const COR = {
  enviado: 'bg-n-teal-3 text-n-teal-11',
  ensaio: 'bg-n-amber-3 text-n-amber-11',
  recusado: 'bg-n-ruby-3 text-n-ruby-11',
  falhou: 'bg-n-ruby-3 text-n-ruby-11',
};
const COR_ENTREGA = {
  entregue: 'text-n-teal-11',
  lida: 'text-n-teal-11',
  recusada: 'text-n-ruby-11',
};

const integracoes = ref([]);
const filtro = ref({
  sgp_id: '',
  chave: '',
  estado: '',
  dias: 7,
  recusadas_pela_meta: false,
});
const pagina = ref(1);
const dados = ref(null);
const carregando = ref(true);
const erro = ref('');

const quando = iso =>
  new Date(iso).toLocaleString('pt-BR', {
    dateStyle: 'short',
    timeStyle: 'short',
  });

const carrega = async () => {
  carregando.value = true;
  try {
    const { data } = await api.value.disparos({
      ...filtro.value,
      recusadas_pela_meta: filtro.value.recusadas_pela_meta ? 1 : 0,
      pagina: pagina.value,
    });
    dados.value = data;
    erro.value = '';
  } catch (error) {
    erro.value = erroDaApi(error, t('NEUXS_NEXUS.DISPAROS.ERRO'));
  } finally {
    carregando.value = false;
  }
};

const carregaIntegracoes = async () => {
  try {
    const { data } = await api.value.gateway();
    integracoes.value = data.integracoes;
  } catch {
    integracoes.value = [];
  }
};

watch(
  () => props.accountId,
  () => {
    carregaIntegracoes();
    carrega();
  },
  { immediate: true }
);
watch(
  filtro,
  () => {
    pagina.value = 1;
    carrega();
  },
  { deep: true }
);
const vaiPara = n => {
  pagina.value = n;
  carrega();
};
</script>

<template>
  <div class="flex flex-col gap-4">
    <form class="flex flex-wrap items-end gap-3" @submit.prevent="carrega">
      <label
        v-if="integracoes.length > 1"
        class="flex flex-col gap-1 text-xs text-n-slate-11"
      >
        {{ t('NEUXS_NEXUS.GATEWAY.ENDERECO') }}
        <select v-model="filtro.sgp_id" class="!mb-0">
          <option value="">{{ t('NEUXS_NEXUS.DISPAROS.TODAS') }}</option>
          <option v-for="i in integracoes" :key="i.id" :value="i.id">
            {{ i.nome }}
          </option>
        </select>
      </label>
      <label class="flex flex-col gap-1 text-xs text-n-slate-11">
        {{ t('NEUXS_NEXUS.DISPAROS.MODELO') }}
        <input
          v-model.lazy="filtro.chave"
          type="text"
          :placeholder="EXEMPLO_DE_CHAVE"
          class="!mb-0"
        />
      </label>
      <label class="flex flex-col gap-1 text-xs text-n-slate-11">
        {{ t('NEUXS_NEXUS.DISPAROS.DESFECHO') }}
        <select v-model="filtro.estado" class="!mb-0">
          <option value="">{{ t('NEUXS_NEXUS.DISPAROS.TODOS') }}</option>
          <option v-for="estado in ESTADOS" :key="estado" :value="estado">
            {{ t(`NEUXS_NEXUS.DISPAROS.ESTADOS.${estado}`) }}
          </option>
        </select>
      </label>
      <label class="flex flex-col gap-1 text-xs text-n-slate-11">
        {{ t('NEUXS_NEXUS.DISPAROS.PERIODO') }}
        <select v-model="filtro.dias" class="!mb-0">
          <option v-for="dias in PERIODOS" :key="dias" :value="dias">
            {{
              dias === 1
                ? t('NEUXS_NEXUS.DISPAROS.DIA')
                : t('NEUXS_NEXUS.DISPAROS.DIAS', { n: dias })
            }}
          </option>
        </select>
      </label>
      <label class="flex items-center gap-2 pb-2 text-xs text-n-slate-11">
        <input
          v-model="filtro.recusadas_pela_meta"
          type="checkbox"
          class="!mb-0"
        />
        {{ t('NEUXS_NEXUS.DISPAROS.SO_RECUSADAS') }}
      </label>
    </form>

    <div v-if="carregando && !dados" class="flex justify-center py-10">
      <Spinner />
    </div>
    <p v-else-if="erro" class="text-sm text-n-ruby-11">{{ erro }}</p>

    <template v-else-if="dados">
      <div class="flex flex-wrap gap-2">
        <div
          v-for="estado in RESUMO"
          :key="estado"
          class="px-3 py-2 border rounded-lg border-n-weak min-w-24"
        >
          <strong class="block text-heading-3 text-n-slate-12">
            {{ dados.por_estado[estado] || 0 }}
          </strong>
          <span class="text-xs text-n-slate-11">
            {{ t(`NEUXS_NEXUS.DISPAROS.ESTADOS.${estado}`) }}
          </span>
        </div>
        <div class="px-3 py-2 border rounded-lg border-n-weak min-w-24">
          <strong class="block text-heading-3 text-n-ruby-11">
            {{ dados.recusadas_pela_meta }}
          </strong>
          <span class="text-xs text-n-slate-11">
            {{ t('NEUXS_NEXUS.DISPAROS.RECUSADAS') }}
          </span>
        </div>
        <div class="px-3 py-2 border rounded-lg border-n-weak min-w-24">
          <strong class="block text-heading-3 text-n-slate-12">
            {{ dados.na_fila }}
          </strong>
          <span class="text-xs text-n-slate-11">
            {{ t('NEUXS_NEXUS.DISPAROS.NA_FILA') }}
          </span>
        </div>
      </div>
      <p v-if="dados.freio" class="text-sm text-n-amber-11">
        {{ t('NEUXS_NEXUS.DISPAROS.FREIO') }}
      </p>

      <p v-if="!dados.envios.length" class="text-sm text-n-slate-11">
        {{ t('NEUXS_NEXUS.DISPAROS.VAZIO') }}
      </p>
      <ul v-else class="flex flex-col divide-y divide-n-weak">
        <li
          v-for="envio in dados.envios"
          :key="envio.id"
          class="flex flex-col gap-0.5 py-2"
        >
          <div class="flex flex-wrap items-center gap-2 text-sm">
            <span class="text-xs text-n-slate-10">
              {{ quando(envio.criado_em) }}
            </span>
            <span class="font-medium text-n-slate-12">
              {{ envio.modelo_chave ? `#${envio.modelo_chave}` : '—' }}
            </span>
            <span class="text-n-slate-11">{{ envio.numero || '—' }}</span>
            <span
              class="px-2 text-xs rounded-full"
              :class="COR[envio.estado] || 'bg-n-slate-3 text-n-slate-11'"
            >
              {{ t(`NEUXS_NEXUS.DISPAROS.ESTADOS.${envio.estado}`) }}
            </span>
            <span
              v-if="envio.entrega"
              class="text-xs"
              :class="COR_ENTREGA[envio.entrega] || 'text-n-slate-11'"
            >
              {{ t(`NEUXS_NEXUS.DISPAROS.ENTREGA.${envio.entrega}`) }}
            </span>
            <router-link
              v-if="envio.conversa_id"
              :to="`/app/accounts/${accountId}/conversations/${envio.conversa_id}`"
              class="text-xs underline text-n-slate-11"
            >
              #{{ envio.conversa_id }}
            </router-link>
          </div>
          <span
            v-if="envio.valores.length"
            class="text-xs truncate text-n-slate-10"
          >
            {{ envio.valores.join(' · ') }}
          </span>
          <span v-if="envio.erro" class="text-xs text-n-ruby-11">
            {{ envio.erro }}
          </span>
        </li>
      </ul>

      <div
        v-if="dados.total_paginas > 1"
        class="flex items-center justify-center gap-2"
      >
        <Button
          :label="t('NEUXS_NEXUS.DISPAROS.ANTERIOR')"
          size="xs"
          variant="ghost"
          color="slate"
          :disabled="dados.pagina <= 1"
          @click="vaiPara(dados.pagina - 1)"
        />
        <span class="text-xs text-n-slate-11">
          {{
            t('NEUXS_NEXUS.DISPAROS.PAGINA', {
              pagina: dados.pagina,
              total: dados.total_paginas,
            })
          }}
        </span>
        <Button
          :label="t('NEUXS_NEXUS.DISPAROS.PROXIMA')"
          size="xs"
          variant="ghost"
          color="slate"
          :disabled="dados.pagina >= dados.total_paginas"
          @click="vaiPara(dados.pagina + 1)"
        />
      </div>
    </template>
  </div>
</template>
